import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useSiteSettings } from '@/lib/hooks/useSiteSettings';

/**
 * Provides default Open Graph, Twitter Card, and other SEO meta tags.
 * Place this in _app.tsx so every page gets sensible defaults.
 *
 * Individual pages can override any of these by rendering their own <Head>
 * with the same meta property — Next.js deduplicates by key.
 */
export function DefaultSeo() {
  const $ = _c(56);
  const {
    settings
  } = useSiteSettings();
  const router = useRouter();
  const siteUrl = `https://${settings.domain}`;
  let t0;
  if ($[0] !== router.asPath) {
    t0 = router.asPath.split("?");
    $[0] = router.asPath;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const canonicalUrl = `${siteUrl}${t0[0]}`;
  let t1;
  if ($[2] !== settings.ogImageUrl || $[3] !== siteUrl) {
    t1 = settings.ogImageUrl?.startsWith("http") ? settings.ogImageUrl : `${siteUrl}${settings.ogImageUrl}`;
    $[2] = settings.ogImageUrl;
    $[3] = siteUrl;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const ogImage = t1;
  const t2 = `${settings.siteName} ${settings.seoTitleSuffix}`;
  let t3;
  if ($[5] !== t2) {
    t3 = <title key="title">{t2}</title>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== settings.seoDefaultDescription) {
    t4 = <meta key="description" name="description" content={settings.seoDefaultDescription} />;
    $[7] = settings.seoDefaultDescription;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== settings.siteName) {
    t5 = <meta key="author" name="author" content={settings.siteName} />;
    $[9] = settings.siteName;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== canonicalUrl) {
    t6 = <link key="canonical" rel="canonical" href={canonicalUrl} />;
    $[11] = canonicalUrl;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <meta key="og:type" property="og:type" content="website" />;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  let t9;
  if ($[14] !== settings.siteName) {
    t8 = <meta key="og:site_name" property="og:site_name" content={settings.siteName} />;
    t9 = <meta key="og:title" property="og:title" content={settings.siteName} />;
    $[14] = settings.siteName;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== settings.seoDefaultDescription) {
    t10 = <meta key="og:description" property="og:description" content={settings.seoDefaultDescription} />;
    $[17] = settings.seoDefaultDescription;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== canonicalUrl) {
    t11 = <meta key="og:url" property="og:url" content={canonicalUrl} />;
    $[19] = canonicalUrl;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== ogImage) {
    t12 = <meta key="og:image" property="og:image" content={ogImage} />;
    $[21] = ogImage;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <meta key="og:image:width" property="og:image:width" content="1200" />;
    t14 = <meta key="og:image:height" property="og:image:height" content="630" />;
    $[23] = t13;
    $[24] = t14;
  } else {
    t13 = $[23];
    t14 = $[24];
  }
  let t15;
  if ($[25] !== settings.siteName) {
    t15 = <meta key="og:image:alt" property="og:image:alt" content={settings.siteName} />;
    $[25] = settings.siteName;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== settings.locale) {
    t16 = <meta key="og:locale" property="og:locale" content={settings.locale} />;
    $[27] = settings.locale;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <meta key="twitter:card" name="twitter:card" content="summary_large_image" />;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== settings.siteName) {
    t18 = <meta key="twitter:title" name="twitter:title" content={settings.siteName} />;
    $[30] = settings.siteName;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== settings.seoDefaultDescription) {
    t19 = <meta key="twitter:description" name="twitter:description" content={settings.seoDefaultDescription} />;
    $[32] = settings.seoDefaultDescription;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== ogImage) {
    t20 = <meta key="twitter:image" name="twitter:image" content={ogImage} />;
    $[34] = ogImage;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== settings.siteName) {
    t21 = <meta key="twitter:image:alt" name="twitter:image:alt" content={settings.siteName} />;
    $[36] = settings.siteName;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  let t23;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <meta key="robots" name="robots" content="index, follow" />;
    t23 = <meta key="googlebot" name="googlebot" content="index, follow, max-video-preview:-1, max-image-preview:large, max-snippet:-1" />;
    $[38] = t22;
    $[39] = t23;
  } else {
    t22 = $[38];
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t15 || $[44] !== t16 || $[45] !== t18 || $[46] !== t19 || $[47] !== t20 || $[48] !== t21 || $[49] !== t3 || $[50] !== t4 || $[51] !== t5 || $[52] !== t6 || $[53] !== t8 || $[54] !== t9) {
    t24 = <Head>{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}</Head>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
    $[49] = t3;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t8;
    $[54] = t9;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  return t24;
}